import { useState, useRef } from 'react'
import { quickUploadReceipts } from '../api'

// Minimal, mobile-only receipt capture page. Deliberately makes ZERO data calls on
// load (no transactions / businesses), so opening it never wakes the Serverless DB.
// It only POSTs the photo to the SQL-free /receipts/quick-upload endpoint, which drops
// it to blob storage + an intake queue; the backend processes it later.

type Item = { id: number; name: string; state: 'uploading' | 'done' | 'error'; error?: string }

export default function ReceiptsMobile() {
  const [items, setItems] = useState<Item[]>([])
  const idRef = useRef(0)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return
    const picked = Array.from(files)
    const entries: Item[] = picked.map(f => ({ id: ++idRef.current, name: f.name || 'photo.jpg', state: 'uploading' }))
    setItems(prev => [...entries, ...prev])

    // Upload one at a time so a slow connection still shows progress per file.
    for (let i = 0; i < picked.length; i++) {
      const entry = entries[i]
      try {
        const res = await quickUploadReceipts([picked[i]])
        const err = res.errors?.[0]?.error
        setItems(prev => prev.map(it => it.id === entry.id
          ? { ...it, state: err ? 'error' : 'done', error: err }
          : it))
      } catch (e) {
        setItems(prev => prev.map(it => it.id === entry.id
          ? { ...it, state: 'error', error: e instanceof Error ? e.message : 'Upload failed' }
          : it))
      }
    }
    if (inputRef.current) inputRef.current.value = ''
  }

  const icon = (s: Item['state']) =>
    s === 'uploading' ? <span className="spinner-border spinner-border-sm" style={{ width: 14, height: 14 }} />
    : s === 'done'    ? <i className="bi bi-check-circle-fill" style={{ color: '#16a34a' }} />
    :                   <i className="bi bi-exclamation-circle-fill" style={{ color: '#ef4444' }} />

  return (
    <div style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '1.5rem 1rem', gap: '1.25rem' }}>
      <div style={{ textAlign: 'center', marginTop: '1rem' }}>
        <h1 className="pb-page-title mb-1" style={{ fontSize: '1.4rem' }}>Receipt Capture</h1>
        <div style={{ color: 'var(--pb-muted)', fontSize: '0.85rem' }}>Snap a receipt — it's processed in the background.</div>
      </div>

      <label style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.75rem',
        width: '100%', maxWidth: 420, minHeight: 200, cursor: 'pointer',
        border: '2px dashed var(--pb-border)', borderRadius: 18, background: 'var(--pb-glass-bg)',
        color: 'var(--pb-primary)', textAlign: 'center', padding: '1.5rem',
      }}>
        <i className="bi bi-camera" style={{ fontSize: '3rem' }} />
        <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>Take / choose photo</div>
        <div style={{ color: 'var(--pb-muted)', fontSize: '0.8rem' }}>JPEG · PNG · PDF · you can pick several</div>
        <input
          ref={inputRef}
          type="file"
          accept="image/*,application/pdf"
          capture="environment"
          multiple
          style={{ display: 'none' }}
          onChange={e => handleFiles(e.target.files)}
        />
      </label>

      {items.length > 0 && (
        <div style={{ width: '100%', maxWidth: 420, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {items.map(it => (
            <div key={it.id} style={{
              display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.6rem 0.85rem',
              borderRadius: 12, border: '1px solid var(--pb-border)', background: 'var(--pb-glass-bg)', fontSize: '0.85rem',
            }}>
              {icon(it.state)}
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.name}</span>
              <span style={{ fontSize: '0.75rem', color: it.state === 'error' ? '#ef4444' : 'var(--pb-muted)' }}>
                {it.state === 'uploading' ? 'Uploading…' : it.state === 'done' ? 'Sent' : (it.error ?? 'Failed')}
              </span>
            </div>
          ))}
        </div>
      )}

      <div style={{ color: 'var(--pb-muted)', fontSize: '0.75rem', textAlign: 'center', maxWidth: 420, marginTop: 'auto', paddingTop: '1rem' }}>
        You can close this page after a receipt shows “Sent” — extraction and matching happen on the server.
      </div>
    </div>
  )
}
